/**
 * RequireCap — a route-level gate (PERM-ENFORCE-01, review 2026-11-02 §6).
 *
 * Wrap a page in `<RequireCap any={routeCaps("/board")}>` and the page renders
 * only for a login that holds ANY of the listed capabilities (the admin wildcard
 * always passes; an empty list means "signed in is enough"). Until the
 * permission state is known nothing renders — so a gated screen never flashes
 * in and then vanishes. A login without the capability sees a quiet "not for
 * this login" card and a way back, never a blank screen and never the page —
 * or, with `elseTo`, is sent somewhere that is theirs (a tech landing on the
 * office shell goes to /m).
 *
 * The route map lives in routeCaps.ts; App.tsx and the Sidebar both read it.
 */
import type { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { usePermissions } from "./PermissionProvider";
import { useAuth } from "@/lib/auth";

export function RequireCap({
  any,
  children,
  backTo = "/",
  elseTo,
}: {
  any: readonly string[];
  children: ReactNode;
  backTo?: string;
  /** Where to send a login that lacks the capability instead of showing the card. */
  elseTo?: string;
}) {
  const { canAny, ready, isAdmin } = usePermissions();
  const { signOut } = useAuth();
  if (!ready) return null;
  const ok = isAdmin || any.length === 0 || canAny(any);
  if (ok) return <>{children}</>;
  if (elseTo) return <Navigate to={elseTo} replace />;
  const signOutInstead = backTo === "/login";
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm rounded-2xl border border-hairline bg-white p-6 text-center shadow-e1">
        <span className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-slate-100 text-slate-500">
          <ShieldOff className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <h1 className="mt-3 text-[17px] font-bold text-slate-900">Not for This Login</h1>
        <p className="mt-1 text-[13.5px] text-slate-500">This part of Pegasus isn't turned on for your login. The office can change that under Settings → People & Access.</p>
        {signOutInstead ? (
          <button type="button" onClick={() => void signOut()} className="mt-4 inline-flex h-9 items-center rounded-full bg-brand-700 px-4 text-[12.5px] font-semibold text-white hover:bg-brand-800">
            Sign Out
          </button>
        ) : (
          <Link to={backTo} className="mt-4 inline-flex h-9 items-center rounded-full bg-brand-700 px-4 text-[12.5px] font-semibold text-white hover:bg-brand-800">
            Back
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * The office shell's own gate: office.access opens it; a login that only has the
 * field app is sent to /m instead of a dead end.
 */
export function RequireOffice({ children }: { children: ReactNode }) {
  const { can, ready } = usePermissions();
  if (!ready) return null;
  const fieldOnly = !can("office.access") && (can("mobile.access") || can("mobile.manager"));
  return (
    <RequireCap any={["office.access"]} elseTo={fieldOnly ? "/m" : undefined} backTo="/login">
      {children}
    </RequireCap>
  );
}
